import {DEFAULT_QUERY_CONCURRENCY} from '../query-concurrency-scaling';
import {QueryPermitTrackEvent} from './tracking';

export const QUERY_PERMIT_HEARTBEAT_MS = 30_000;
export const QUERY_PERMIT_LIVENESS_THRESHOLD_MS = 60_000;
export const QUERY_PERMIT_LIVENESS_SWEEP_MS = 15_000;
export const QUERY_PERMIT_MAX_HOLD_MS = 90_000;
// Cadence at which a tab re-evaluates a queued request's live priority (e.g. its card's
// viewport distance) and pushes an update-priority to the worker if it changed. Polling
// instead of scroll/resize hooks keeps priority tracking framework-agnostic. Stops once
// the permit is granted.
export const QUERY_PERMIT_PRIORITY_POLL_MS = 500;
// Padding above the scaled main-query concurrency for the faster requests (the per-card
// query_metadata calls + a couple upfront properties/events requests). Those run ~4x faster
// than the main query (median ~106ms vs ~439ms) so they free their permit long before a main
// query finishes and don't each need a dedicated slot; ~3 slots absorb them by time-weight
// (12 fast requests x ~0.24 occupancy).
export const QUERY_PERMIT_FAST_QUERY_PAD = 3;
// Cross-tab headroom above the raw per-project concurrency limits. The semaphore turns each
// cap into a cross-tab TOTAL, which ran too tight for heavy multi-tab users (investing.com,
// Viber) whose per-tab expectations predate the shared pool. A little slack is added to relieve
// that, to be tuned from standard_mode.query_pool_stats telemetry during rollout. Standard
// projects get more slack than the constrained-backend tiers, which stay near parity because
// over-provisioning their backends is riskier.
export const QUERY_PERMIT_STANDARD_HEADROOM = 2;
export const QUERY_PERMIT_LIMITED_HEADROOM = 1;
// Max concurrent /api/query/* requests across all tabs of the origin. Sized for one dashboard's
// load burst: (10 permits for the slow main queries — the shared DEFAULT_QUERY_CONCURRENCY,
// matching the app's own dashboards/query-concurrency.ts — plus the fast-query pad and the
// cross-tab headroom).
export const QUERY_PERMIT_GLOBAL_CAP =
  DEFAULT_QUERY_CONCURRENCY + QUERY_PERMIT_FAST_QUERY_PAD + QUERY_PERMIT_STANDARD_HEADROOM;
// Reduced caps for projects with constrained backends, mirroring the LIMITED /
// VERY_LIMITED tiers in getMaxConcurrentQueries (iron/common/report/dashboards/
// query-concurrency.ts). These sit close to the raw per-project concurrency limit (5 / 2) with
// only QUERY_PERMIT_LIMITED_HEADROOM of cross-tab slack — no fast-query padding — because
// over-provisioning these backends is riskier than for standard projects. Resolution lives
// tab-side in client.ts (getQueryPermitGlobalCap), where the project-config constants live;
// the worker treats the value as the pool ceiling.
export const QUERY_PERMIT_LIMITED_CAP = 5 + QUERY_PERMIT_LIMITED_HEADROOM;
export const QUERY_PERMIT_VERY_LIMITED_CAP = 2 + QUERY_PERMIT_LIMITED_HEADROOM;
// Fail-open circuit breaker. The SharedWorker holds every still-queued acquire in memory and
// re-sorts them on each grant, so an unbounded pending queue (a runaway tab flooding thousands
// of requests, or a future bug) could pin the worker's single thread. Once this many acquires
// are waiting across ALL project pools, further acquires are granted immediately — bypassing the
// queue rather than joining it — so the pending queue can't grow past this and the backend +
// browser connection pool become the limiter. This protects the tab from our own bug; it is NOT
// backend load-shedding (that stays upstream, surfaced to the user as a 429 at the card level).
export const QUERY_PERMIT_MAX_PENDING = 1000;

// ---- soft rate throttle ------------------------------------------------------
export const QUERY_PERMIT_SOFT_THROTTLE_RPM = 60;
// Rolling window for rate measurement, sized to absorb the initial dashboard-load burst.
export const QUERY_PERMIT_SOFT_THROTTLE_WINDOW_MS = 16_000;
// Exponential backoff bounds.
export const QUERY_PERMIT_SOFT_THROTTLE_MIN_BACKOFF_MS = 250;
export const QUERY_PERMIT_SOFT_THROTTLE_MAX_BACKOFF_MS = 4_000;
// Quiet gap that fully resets the backoff + rolling window.
export const QUERY_PERMIT_SOFT_THROTTLE_RESET_MS = 4_000;
// How often the worker forwards each project's aggregated metrics to a live tab for tracking.
export const QUERY_PERMIT_METRICS_FLUSH_MS = 30_000;

// ---- priority / rank ---------------------------------------------------------
//
// Queued acquires are served lowest-rank-first (the worker pool sorts with
// MIN_NODE_COMPARATOR). A rank composes three tiers, in precedence order:
//   1. tab tier        — the foreground (currently-viewed) tab outranks background tabs
//   2. pool tier       — the foreground pool outranks the background (prefetch) pool
//   3. viewport dist   — a viewport distance (px); cards nearer the viewport are served first
// Lower wins at every tier. Tab/pool tiers use FOREGROUND=0 < BACKGROUND=1.
export const FOREGROUND = 0;
export const BACKGROUND = 1;

// Weights make each tier strictly dominate the next: any difference in tab tier
// outweighs every pool tier + viewport-distance combination, and so on. The clamp
// ceiling bounds viewportDistance (which can arrive as Number.MAX_VALUE — the
// sentinel getCardDistanceFromViewport returns for offscreen/unknown cards) so the
// composite stays well under Number.MAX_SAFE_INTEGER.
export const QUERY_PERMIT_VIEWPORT_DISTANCE_MAX = 1_000_000;
export const QUERY_PERMIT_POOL_WEIGHT = QUERY_PERMIT_VIEWPORT_DISTANCE_MAX * 10;
export const QUERY_PERMIT_TAB_WEIGHT = QUERY_PERMIT_POOL_WEIGHT * 10;

export interface PermitRank {
  tabTier: number;
  poolTier: number;
  viewportDistance: number;
}

export function composeRank({tabTier, poolTier, viewportDistance}: PermitRank): number {
  const clampedDistance = Math.min(Math.max(viewportDistance, 0), QUERY_PERMIT_VIEWPORT_DISTANCE_MAX);
  return tabTier * QUERY_PERMIT_TAB_WEIGHT + poolTier * QUERY_PERMIT_POOL_WEIGHT + clampedDistance;
}

export enum MessageType {
  // Tab -> worker
  Acquire = `acquire`,
  UpdatePriority = `update-priority`,
  SetTabTier = `set-tab-tier`,
  Cancel = `cancel`,
  Release = `release`,
  Heartbeat = `heartbeat`,
  // Worker -> tab
  Granted = `granted`,
  Reclaimed = `reclaimed`,
  Track = `track`,
}

export interface AcquireMessage {
  type: MessageType.Acquire;
  reqId: string;
  projectId: number;
  // per-project pool ceiling.
  cap: number;
  softThrottle: boolean;
  poolTier: number;
  viewportDistance: number;
}

export type TabToWorkerMessage =
  | AcquireMessage
  // Re-rank a single queued request (e.g. its card scrolled toward/away from the viewport).
  | {type: MessageType.UpdatePriority; reqId: string; poolTier: number; viewportDistance: number}
  // Re-rank every queued + future request from this port/tab (e.g. the tab was foregrounded).
  | {type: MessageType.SetTabTier; tabTier: number}
  // Withdraw a still-queued acquire that the caller abandoned (e.g. its AbortController
  // fired before the permit was granted), giving up its place in line. No-op once granted.
  | {type: MessageType.Cancel; reqId: string}
  | {type: MessageType.Release; reqId: string}
  | {type: MessageType.Heartbeat};

export type WorkerToTabMessage =
  | {type: MessageType.Granted; reqId: string}
  | {type: MessageType.Reclaimed; reqId: string}
  | ({type: MessageType.Track} & QueryPermitTrackEvent);
